import type { Expression, Map } from "azure-maps-control";
import type {
  AllCriteria,
  AndCriteria,
  Criteria,
  DateGreaterCriteria,
  DateGreaterEqualCriteria,
  DateLessCriteria,
  DateLessEqualCriteria,
  EqualCriteria,
  GreaterCriteria,
  GreaterEqualCriteria,
  InCriteria,
  LessCriteria,
  LessEqualCriteria,
  LikeCriteria,
  NoneCriteria,
  NotCriteria,
  NotEqualCriteria,
  OrCriteria,
  TerritoryLevelCriteria,
} from "../model/Criteria";
import {ExpressionSpecification} from "@maplibre/maplibre-gl-style-spec";

export class AzureCriteriaAdapter {

  static convert(criteria: Criteria|undefined|null, map: Map) : Expression | undefined {
    if(!criteria) {
      return undefined;
    }

    if(criteria.$type === "In") {
      return InCriteriaConverter(criteria);
    }

    if(criteria.$type === "Not") {
      return NotCriteriaConverter(criteria, map);
    }

    if(criteria.$type === "Equal") {
      return EqualsCriteriaConverter(criteria);
    }

    if(criteria.$type === "NotEqual") {
      return NotEqualCriteriaConverter(criteria);
    }

    if(criteria.$type === "Greater") {
      return GreaterCriteriaConverter(criteria);
    }

    if(criteria.$type === "GreaterEqual") {
      return GreaterEqualCriteriaConverter(criteria);
    }

    if(criteria.$type === "Less") {
      return LessCriteriaConverter(criteria);
    }

    if(criteria.$type === "LessEqual") {
      return LessEqualCriteriaConverter(criteria);
    }

    if(criteria.$type === "DateGreater") {
      return DateGreaterCriteriaConverter(criteria);
    }

    if(criteria.$type === "DateGreaterEqual") {
      return DateGreaterEqualCriteriaConverter(criteria);
    }

    if(criteria.$type === "DateLess") {
      return DateLessCriteriaConverter(criteria);
    }

    if(criteria.$type === "DateLessEqual") {
      return DateLessEqualCriteriaConverter(criteria);
    }

    if(criteria.$type === "And") {
      return AndCriteriaConverter(criteria, map);
    }

    if(criteria.$type === "Or") {
      return OrCriteriaConverter(criteria, map);
    }

    if(criteria.$type === "None") {
      return NoneCriteriaConverter(criteria);
    }

    if(criteria.$type === "All") {
      return AllCriteriaConverter(criteria);
    }

    if(criteria.$type === "Like") {
      return LikeCriteriaConverter(criteria);
    }

    if(criteria.$type === "TerritoryLevel") {
      return TerritoryLevelCriteriaConverter(criteria);
    }

    console.warn("No matching converter for criteria: ", criteria);
    return undefined;
  }

  static merge(...filters: (Expression|undefined)[]): Expression | undefined {
    const filter: Expression = ["all"];

    filters.forEach(exp => {
      if(exp !== undefined) {
        filter.push(exp);
      }
    });

    if(filter.length === 1) {
      return undefined;
    }

    if(filter.length === 2) {
      return filter[1] as Expression;
    }

    return filter;
  }
}

function InCriteriaConverter(criteria: InCriteria<string|number>) : Expression | undefined {
  if(criteria.$type !== "In" || !criteria.attr || !criteria.values) {
    console.warn("Wrong criteria passed to InCriteriaConverter: ", criteria);
    return undefined;
  }
  return ['in', getPropertyByAttr(criteria.attr), ['literal', criteria.values]]
}

function NotCriteriaConverter(criteria: NotCriteria, map: Map) : Expression | undefined {
  if(criteria.$type !== "Not") {
    console.warn("Wrong criteria passed to NotCriteriaConverter: ", criteria);
    return undefined;
  }

  const internalCriteria = AzureCriteriaAdapter.convert(criteria.criteria, map) as Expression;
  if(!internalCriteria) {
    return undefined;
  }
  return ['!', internalCriteria]
}

function EqualsCriteriaConverter<T>(criteria: EqualCriteria<T>): Expression | undefined {
  if(criteria.$type !== "Equal" || !criteria.attr) {
    console.warn("Wrong criteria passed to EqualsCriteriaConverter: ", criteria);
    return undefined;
  }

  // "Blank" value filter
  if (criteria.value == undefined || criteria.value === "") {
    return ['any', ["!", ['has', criteria.attr]], ['==', ['get', criteria.attr], '']];
  }

  return ['==', getPropertyByAttr(criteria.attr), criteria.value ?? -1];
}

function NotEqualCriteriaConverter<T>(criteria: NotEqualCriteria<T>): Expression | undefined {
  if(criteria.$type !== "NotEqual" || !criteria.attr) {
    console.warn("Wrong criteria passed to NotEqualsCriteriaConverter: ", criteria);
    return undefined;
  }

  // "Blank" value filter
  if (!criteria.value || criteria.value === "") {
    return ['all', ['has', criteria.attr], ['!=', ['get', criteria.attr], '']];
  }

  return ['!=', getPropertyByAttr(criteria.attr), criteria.value ?? -1];
}

function GreaterCriteriaConverter(criteria: GreaterCriteria): Expression | undefined {
  if (criteria.$type !== "Greater" || !criteria.attr || criteria.value == undefined) {
    console.warn("Wrong criteria passed to GreaterCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['>', getPropertyByAttr(criteria.attr), criteria.value];
}

function GreaterEqualCriteriaConverter(criteria: GreaterEqualCriteria): Expression | undefined {
  if (criteria.$type !== "GreaterEqual" || !criteria.attr || criteria.value == undefined) {
    console.warn("Wrong criteria passed to GreaterEqualCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['>=', getPropertyByAttr(criteria.attr), criteria.value];
}

function LessCriteriaConverter(criteria: LessCriteria): Expression | undefined {
  if (criteria.$type !== "Less" || !criteria.attr || criteria.value == undefined) {
    console.warn("Wrong criteria passed to LessCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['<', getPropertyByAttr(criteria.attr), criteria.value];
}

function LessEqualCriteriaConverter(criteria: LessEqualCriteria): Expression | undefined {
  if (criteria.$type !== "LessEqual" || !criteria.attr || criteria.value == undefined) {
    console.warn("Wrong criteria passed to LessEqualCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['<=', getPropertyByAttr(criteria.attr), criteria.value];
}

function DateGreaterCriteriaConverter(criteria: DateGreaterCriteria): Expression | undefined {
  if(criteria.$type !== "DateGreater" || !criteria.attr || !criteria.value) {
    console.warn("Wrong criteria passed to DateGreaterCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['>', getPropertyByAttr(criteria.attr), criteria.value];
}

function DateGreaterEqualCriteriaConverter(criteria: DateGreaterEqualCriteria): Expression | undefined {
  if(criteria.$type !== "DateGreaterEqual" || !criteria.attr || !criteria.value) {
    console.warn("Wrong criteria passed to DateGreaterEqualCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['>=', getPropertyByAttr(criteria.attr), criteria.value];
}

function DateLessCriteriaConverter(criteria: DateLessCriteria): Expression | undefined {
  if(criteria.$type !== "DateLess" || !criteria.attr || !criteria.value) {
    console.warn("Wrong criteria passed to DateLessCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['<', getPropertyByAttr(criteria.attr), criteria.value];
}

function DateLessEqualCriteriaConverter(criteria: DateLessEqualCriteria): Expression | undefined {
  if(criteria.$type !== "DateLessEqual" || !criteria.attr || !criteria.value) {
    console.warn("Wrong criteria passed to DateLessEqualCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['<=', getPropertyByAttr(criteria.attr), criteria.value];
}

function AndCriteriaConverter(criteria: AndCriteria, map: Map): Expression | undefined {
  if(criteria.$type !== "And") {
    console.warn("Wrong criteria passed to AndCriteriaConverter: ", criteria);
    return undefined;
  }

  const internalCriteria = criteria.criteria.map(c => AzureCriteriaAdapter.convert(c, map)).filter(ex => ex !== undefined) as Expression[];
  if(internalCriteria.length <=1) {
    // return undefined if the internalCriteria is empty or unwrap in the case of a single expression
    return internalCriteria[1];
  }
  return ['all', ...internalCriteria];
}

function OrCriteriaConverter(criteria: OrCriteria, map: Map): Expression | undefined {
  if(criteria.$type !== "Or") {
    console.warn("Wrong criteria passed to OrCriteriaConverter: ", criteria);
    return undefined;
  }

  const internalCriteria = criteria.criteria.map(c => AzureCriteriaAdapter.convert(c, map)).filter(ex => ex !== undefined) as Expression[]
  if(internalCriteria.length <=1) {
    // return undefined if the internalCriteria is empty or unwrap in the case of a single expression
    return internalCriteria[1];
  }
  return ['any', ...internalCriteria];
}

function NoneCriteriaConverter(criteria: NoneCriteria): Expression | undefined {
  if(criteria.$type !== "None") {
    console.warn("Wrong criteria passed to NoneCriteriaConverter: ", criteria);
    return undefined;
  }
  return ['!', true];
}

function AllCriteriaConverter(criteria: AllCriteria): Expression | undefined {
  if(criteria.$type !== "All") {
    console.warn("Wrong criteria passed to AllCriteriaConverter: ", criteria);
    return undefined;
  }
  return ['==', true, true];
}

function LikeCriteriaConverter(criteria: LikeCriteria): Expression | undefined {
  if(criteria.$type !== "Like" || !criteria.attr || !criteria.value) {
    console.warn("Wrong criteria passed to LikeCriteriaConverter: ", criteria);
    return undefined;
  }

  return ['>', ['index-of', criteria.value.toUpperCase(), ['upcase', getPropertyByAttr(criteria.attr)]], -1];
}

function TerritoryLevelCriteriaConverter(criteria: TerritoryLevelCriteria): Expression | undefined {
  if(criteria.$type !== "TerritoryLevel" || !criteria.level) {
    console.warn("Wrong criteria passed to TerritoryLevelCriteriaConverter: ", criteria);
    return undefined;
  }

  let filter: Expression = ['==', ['get', 'ES_LEVEL'], criteria.level];

  if(criteria.parent != null){
    filter = ['all', filter, ['==', ['get', 'ES_PARENT_ID'], criteria.parent]];
  }
  return filter;
}

function getPropertyByAttr(attr: string){
  if(attr === "__id"){
    return ["id"] as ExpressionSpecification
  }
  
  return ["get", attr] as ExpressionSpecification;
}
